import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { WebEDIRowData } from 'support/types/webediTable';
import { Button } from 'components/Form/Button';

import { useDataTableActions } from '../../../contexts/DataTableContext';
import { isProcurosAddedValue } from '../cellHelpers';

export type Props = CellContext<WebEDIRowData, unknown>;

export const DeleteCell = ({ table, row, column }: Props) => {
  const { t } = useTranslation();
  const tableDataPrefix = table.options.meta?.dataPrefix ?? '';
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const { setValue, remove } = useDataTableActions();

  const quantityField = column?.columnDef.meta?.optionalConfig?.quantityField;

  const absolutePathToQuantityField = useMemo(
    () => (quantityField ? `${tableDataPrefix}.${row.index}.${quantityField}` : ''),
    [quantityField, tableDataPrefix, row.index],
  );

  const orderedQuantityPath = useMemo(
    () => `${tableDataPrefix}.${row.index}.ordered_quantity`,
    [tableDataPrefix, row.index],
  );

  const isAdded = isProcurosAddedValue(row);
  const [value, orderedQuantityValue] = useWatch({
    name: [absolutePathToQuantityField, orderedQuantityPath],
  });

  const handleDelete = useCallback(() => {
    if (isAdded || !absolutePathToQuantityField) {
      remove(row.index);
    } else {
      setValue(absolutePathToQuantityField, 0);
    }
  }, [absolutePathToQuantityField, isAdded, remove, row.index, setValue]);

  const handleRestore = useCallback(() => {
    setValue(absolutePathToQuantityField, orderedQuantityValue);
  }, [setValue, absolutePathToQuantityField, orderedQuantityValue]);

  const buttonProps = {
    iconOnly: true,
    size: 'extra-small' as const,
    variant: 'minimal' as const,
    className: 'mx-1',
  };

  const toBeRestored = value === 0 && value !== orderedQuantityValue;

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex justify-center px-3">
        {toBeRestored ? (
          <Button
            {...buttonProps}
            LeftIcon={ArrowPathIcon}
            onClick={handleRestore}
            analyticsId="table_item_restore"
            aria-label={t('webedi:deleteRow.restore')}
            disabled={isSubmitting}
          >
            {t('webedi:deleteRow.restore')}
          </Button>
        ) : (
          <Button
            {...buttonProps}
            LeftIcon={XMarkIcon}
            onClick={handleDelete}
            analyticsId="table_item_remove"
            aria-label={t('webedi:deleteRow.remove')}
            disabled={isSubmitting}
          >
            {t('webedi:deleteRow.remove')}
          </Button>
        )}
      </div>
    </div>
  );
};
